import { useState } from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { UserPlus, X } from "lucide-react";
import { api } from "@/lib/api";
import { useDirectory } from "@/lib/lookups";
import { Avatar, useToast, Modal } from "@/components/ui";
import { Field, Select } from "@/components/fields";
import type { ProjectBundle } from "../ProjectDetail";

const ROLE_LABEL: Record<string, string> = { owner: "Project Owner", manager: "Project Manager", member: "Team Member", viewer: "Viewer" };

export function Team({ bundle }: { bundle: ProjectBundle }) {
  const qc = useQueryClient();
  const toast = useToast();
  const directory = useDirectory();
  const [adding, setAdding] = useState(false);
  const canManage = bundle.access.isManager || bundle.access.isOwner || bundle.access.canViewAll;

  const invalidate = () => qc.invalidateQueries({ queryKey: ["project", bundle.project.id] });
  const addMember = useMutation({
    mutationFn: (b: { userId: string; projectRole: string }) => api.post(`/projects/${bundle.project.id}/members`, b),
    onSuccess: () => { invalidate(); toast.push("success", "Member added"); setAdding(false); },
  });
  const removeMember = useMutation({
    mutationFn: (userId: string) => api.del(`/projects/${bundle.project.id}/members/${userId}`),
    onSuccess: () => { invalidate(); toast.push("success", "Member removed"); },
  });

  const memberIds = new Set(bundle.members.map((m) => m.userId));

  return (
    <div className="max-w-2xl">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold">Project team ({bundle.members.length})</h3>
        {canManage && <button className="btn-primary" onClick={() => setAdding(true)}><UserPlus size={15} /> Add member</button>}
      </div>
      <div className="card divide-y divide-line p-0">
        {bundle.members.map((m) => (
          <div key={m.userId} className="flex items-center gap-3 px-4 py-3">
            <Avatar name={m.fullName} color={m.avatarColor} size={34} />
            <div className="flex-1">
              <p className="text-sm font-medium">{m.fullName}</p>
              <p className="text-xs text-ink-faint">{m.jobTitle || m.username}</p>
            </div>
            <span className="chip bg-surface-2 text-ink-soft">{ROLE_LABEL[m.projectRole] || m.projectRole}</span>
            {canManage && m.projectRole !== "owner" && m.projectRole !== "manager" && (
              <button className="btn-ghost !p-1 text-ink-faint hover:text-st-delayed" onClick={() => removeMember.mutate(m.userId)}><X size={15} /></button>
            )}
          </div>
        ))}
      </div>

      {adding && (
        <AddMemberModal
          directory={directory.filter((u) => !memberIds.has(u.id))}
          onClose={() => setAdding(false)}
          onAdd={(userId, projectRole) => addMember.mutate({ userId, projectRole })}
        />
      )}
    </div>
  );
}

function AddMemberModal({ directory, onClose, onAdd }: {
  directory: { id: string; fullName: string; jobTitle: string | null }[];
  onClose: () => void; onAdd: (userId: string, role: string) => void;
}) {
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState("member");
  return (
    <Modal open onClose={onClose} title="Add team member"
      footer={<>
        <button className="btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!userId} onClick={() => onAdd(userId, role)}>Add</button>
      </>}>
      <div className="space-y-3.5">
        <Field label="Person">
          <Select value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">— select —</option>
            {directory.map((u) => <option key={u.id} value={u.id}>{u.fullName}{u.jobTitle ? ` — ${u.jobTitle}` : ""}</option>)}
          </Select>
        </Field>
        <Field label="Project role">
          <Select value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="member">Team Member — sees timeline, updates own tasks</option>
            <option value="viewer">Viewer — read only</option>
          </Select>
        </Field>
      </div>
    </Modal>
  );
}
